import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '../contexts/AuthContext';

// Preferencias do painel /monitor/uso-sistemas. Persistidas no backend
// (tabela singleton dbo.siti_monitor_uso_config) - configuracao UNICA para
// todos os usuarios do Portal TI. Quem muda, muda pra todo mundo.
//
// Estrategia:
// - GET /uso-sistemas/config no mount, e tambem em refetch periodico junto
//   com o polling do painel (refresh manual).
// - Mudanca otimista: ao atualizar, aplica na UI imediatamente e dispara
//   PUT em background. Se falhar, reverte e mostra erro.

const PRESET = {
  janelaAtivoOpcoes: [
    { valor: 1,   label: '1 min'  },
    { valor: 5,   label: '5 min'  },
    { valor: 15,  label: '15 min' },
    { valor: 30,  label: '30 min' },
    { valor: 60,  label: '1 hora' },
    { valor: 120, label: '2 horas (tudo)' },
  ],
  pollingOpcoes: [
    { valor: 5,  label: '5s'      },
    { valor: 15, label: '15s'     },
    { valor: 30, label: '30s'     },
    { valor: 60, label: '1 min'   },
    { valor: 0,  label: 'Manual'  },
  ],
};

const DEFAULTS = {
  janelaAtivoMin: 15,
  verdeAteSeg:    60,
  amareloAteSeg:  300,
  pollingSeg:     15,
  modoCompacto:   false,
  mostrarResumo:  true,
  esconderCinzas: false,
};

// Backend usa snake_case; UI usa camelCase. Conversores nos dois sentidos.
function deBackend(row) {
  if (!row) return DEFAULTS;
  return {
    janelaAtivoMin: row.janela_ativo_min,
    verdeAteSeg:    row.verde_ate_seg,
    amareloAteSeg:  row.amarelo_ate_seg,
    pollingSeg:     row.polling_seg,
    modoCompacto:   !!row.modo_compacto,
    mostrarResumo:  !!row.mostrar_resumo,
    esconderCinzas: !!row.esconder_cinzas,
    _updatedAt:     row.updated_at || null,
    _updatedBy:     row.updated_by || null,
  };
}

function paraBackend(cfg) {
  return {
    janela_ativo_min: cfg.janelaAtivoMin,
    verde_ate_seg:    cfg.verdeAteSeg,
    amarelo_ate_seg:  cfg.amareloAteSeg,
    polling_seg:      cfg.pollingSeg,
    modo_compacto:    cfg.modoCompacto,
    mostrar_resumo:   cfg.mostrarResumo,
    esconder_cinzas:  cfg.esconderCinzas,
  };
}

export function useMonitorConfig() {
  const [config, setConfig] = useState(DEFAULTS);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const mounted = useRef(true);

  useEffect(() => () => { mounted.current = false; }, []);

  // Busca a config do backend. Tambem usada como refresh periodico (chamado
  // pelo proprio painel junto com os outros polls).
  const recarregar = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/uso-sistemas/config`);
      if (mounted.current) {
        setConfig(deBackend(res.data?.data));
        setErro('');
      }
    } catch (_) {
      if (mounted.current) setErro('Nao foi possivel carregar a configuracao.');
    } finally {
      if (mounted.current) setCarregando(false);
    }
  }, []);

  useEffect(() => { recarregar(); }, [recarregar]);

  // Atualizacao otimista: aplica na UI, dispara PUT, reverte se falhar.
  const atualizar = useCallback(async (patch) => {
    const anterior = config;
    const novo = { ...config, ...patch };
    setConfig(novo);
    try {
      const res = await axios.put(`${API}/uso-sistemas/config`, paraBackend(novo));
      if (mounted.current) setConfig(deBackend(res.data?.data));
    } catch (e) {
      if (mounted.current) {
        setConfig(anterior);
        setErro(e?.response?.data?.message || 'Falha ao salvar configuracao.');
      }
    }
  }, [config]);

  const resetar = useCallback(async () => {
    return atualizar(DEFAULTS);
  }, [atualizar]);

  return { config, atualizar, resetar, recarregar, carregando, erro, PRESET };
}
